import React, { useState } from 'react';
import './Hero.css';
import { downloadTikTokVideo, extractVideoInfo } from '../services/tiktokApi';
import { downloadYouTubeVideo, extractYouTubeVideoInfo } from '../services/youtubeApi';
import { validateUrl, getPlatformName } from '../utils/urlDetector';
import VideoResult from './VideoResult';
import PlatformBadges from './PlatformBadges';

const Hero = ({ selectedPlatform: propSelectedPlatform, onPlatformChange }) => {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [videoData, setVideoData] = useState(null);
  const [platform, setPlatform] = useState(null);
  
  // Use prop if provided, otherwise use local state
  const [localSelectedPlatform, setLocalSelectedPlatform] = useState('tiktok');
  const selectedPlatform = propSelectedPlatform || localSelectedPlatform;
  
  // Update placeholder based on selected platform
  const placeholder = selectedPlatform === 'tiktok' 
    ? 'Paste TikTok video link here...' 
    : 'Paste YouTube video link here...';

  const handlePlatformSelect = (platformName) => {
    if (onPlatformChange) {
      onPlatformChange(platformName);
    } else {
      setLocalSelectedPlatform(platformName);
    }
    setError(null);
    setVideoData(null);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setUrl(text);
      setError(null);
    } catch (err) {
      console.error('Failed to read clipboard:', err);
      alert('Please allow clipboard access or paste manually using Ctrl+V');
    }
  };

  const handleDownload = async () => {
    // Validate URL
    const validation = validateUrl(url);
    
    if (!validation.valid) {
      setError(validation.message);
      return;
    }

    setLoading(true);
    setError(null);
    setVideoData(null);
    setPlatform(validation.platform);

    try {
      let response;
      let extractedData;

      // Handle different platforms
      if (validation.platform === 'tiktok') {
        response = await downloadTikTokVideo(url);
        extractedData = extractVideoInfo(response);
      } else if (validation.platform === 'youtube') {
        response = await downloadYouTubeVideo(url);
        extractedData = extractYouTubeVideoInfo(response);
      } else {
        throw new Error(`${getPlatformName(validation.platform)} is not supported yet`);
      }

      setVideoData(extractedData);
    } catch (err) {
      setError(`Failed to fetch video. Please check the URL and try again.`);
      console.error('Download error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleDownload();
    }
  };

  return (
    <section className="hero">
      <div className="hero-container">
        <h1 className="hero-title">Free YouTube & TikTok Video Downloader</h1>
        <p className="hero-subtitle">Download HD videos from YouTube and TikTok - Fast, Free & No Watermark</p>
        <PlatformBadges 
          selectedPlatform={selectedPlatform}
          onPlatformSelect={handlePlatformSelect}
        />
        
        <div className="download-form">
          <input
            type="text"
            className="url-input"
            placeholder={placeholder}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
          />
          <button className="paste-btn" onClick={handlePaste} disabled={loading}>
            📋 Paste
          </button>
          <button className="download-btn" onClick={handleDownload} disabled={loading}>
            {loading ? 'Processing...' : 'Download'}
          </button>
        </div>

        {loading && (
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {videoData && !loading && (
          <VideoResult videoData={videoData} platform={platform} />
        )}
      </div>
      
      {/* How to Download Tutorial - Right Below URL Input */}
      {!videoData && <TutorialSection />}
    </section>
  );
};

// Tutorial Section - 3 Screenshots with Descriptions
const TutorialSection = () => {
  return (
    <div className="tutorial-section">
      <div className="tutorial-container">
        <h2 className="tutorial-title">How to Download YouTube & TikTok Videos</h2>
        <p className="tutorial-intro">
          Follow the steps shown below to easily download any YouTube or TikTok video using our tool. 
          Each screenshot highlights the exact button or action you need to use.
        </p>
        
        <div className="tutorial-screenshots">
          {/* Screenshot 1 */}
          <div className="screenshot-item">
            <div className="screenshot-wrapper">
              <img 
                src="/screenshots/12.PNG" 
                alt="Step 1: Paste YouTube or TikTok URL"
                className="screenshot-image"
              />
            </div>
            <div className="screenshot-description">
              <span className="step-badge">Step 1</span>
              <p>
                Paste your YouTube or TikTok video link into the URL input box. 
                You can also press the Paste button to automatically insert the copied link.
              </p>
            </div>
          </div>

          {/* Screenshot 2 */}
          <div className="screenshot-item">
            <div className="screenshot-wrapper">
              <img 
                src="/screenshots/13.PNG" 
                alt="Step 2: Processing video"
                className="screenshot-image"
              />
            </div>
            <div className="screenshot-description">
              <span className="step-badge">Step 2</span>
              <p>
                Click the Process/Download button. Our system will analyze the link and prepare the video for download.
              </p>
            </div>
          </div>

          {/* Screenshot 3 */}
          <div className="screenshot-item">
            <div className="screenshot-wrapper">
              <img 
                src="/screenshots/14.PNG" 
                alt="Step 3: Download video or audio"
                className="screenshot-image"
              />
            </div>
            <div className="screenshot-description">
              <span className="step-badge">Step 3</span>
              <p>
                After processing, choose your preferred format. Click Download MP3 for audio or Download MP4 for full video.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
